import { Link } from "react-router-dom";

import type { PolicyRuleResponse } from "@/api";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { useTranslation } from "@/i18n";
import { formatRelativeTimeID } from "@/lib/format";
import { ROUTES } from "@/lib/constants";

interface PoliciesListProps {
  items: PolicyRuleResponse[];
  isLoading: boolean;
  onDelete: (rule: PolicyRuleResponse) => void;
}

export function PoliciesList({
  items,
  isLoading,
  onDelete,
}: PoliciesListProps) {
  const { t } = useTranslation();
  return (
    <div className="overflow-hidden rounded-lg border border-ocean-100 bg-white">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("policies.table.name")}</TableHead>
            <TableHead>{t("policies.table.slug")}</TableHead>
            <TableHead>{t("policies.table.status")}</TableHead>
            <TableHead className="hidden tablet:table-cell">
              {t("policies.table.priority")}
            </TableHead>
            <TableHead className="hidden tablet:table-cell">
              {t("policies.table.updated")}
            </TableHead>
            <TableHead className="w-12 text-right">
              {t("policies.table.actions")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && items.length === 0 && (
            <>
              {[0, 1, 2].map((i) => (
                <TableRow key={`skeleton-${i}`}>
                  <TableCell colSpan={6} className="py-4">
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                </TableRow>
              ))}
            </>
          )}
          {!isLoading && items.length === 0 && (
            <TableEmpty
              colSpan={6}
              message={t("policies.table.emptyFiltered")}
            />
          )}
          {items.map((rule) => (
            <TableRow key={rule.id}>
              <TableCell>
                <Link
                  to={`${ROUTES.POLICIES}/${rule.id}`}
                  className="font-medium text-ocean-900 hover:text-ocean-600"
                >
                  {rule.name}
                </Link>
                {rule.description && (
                  <div className="mt-0.5 text-xs text-neutral-500">
                    {rule.description}
                  </div>
                )}
              </TableCell>
              <TableCell>
                <code className="rounded bg-neutral-50 px-2 py-1 font-mono text-xs text-neutral-600">
                  {rule.slug}
                </code>
              </TableCell>
              <TableCell>
                {rule.is_enabled ? (
                  <Badge tone="success">{t("policies.status.enabled")}</Badge>
                ) : (
                  <Badge tone="warning">{t("policies.status.disabled")}</Badge>
                )}
              </TableCell>
              <TableCell className="hidden text-neutral-600 tablet:table-cell">
                {rule.priority}
              </TableCell>
              <TableCell className="hidden text-neutral-600 tablet:table-cell">
                {formatRelativeTimeID(rule.updated_at)}
              </TableCell>
              <TableCell className="text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t("policies.actions.deleteRowAriaLabel", {
                    name: rule.name,
                  })}
                  onClick={() => onDelete(rule)}
                >
                  <Trash2 size={14} className="text-danger-600" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
